import type { DefaultValues, UseFormReturn } from 'react-hook-form';
import { queryString } from '@olivierpascal/helpers';
import { Trans, useTranslation } from 'react-i18next';

import type { IContentBlockFormBodyProps } from '~/components/molecules/ContentBlock';
import type { IContainerProps } from '~/components/utils/Container';
import type { IApiInvitation } from '~/lib/api/modules/Invitations';
import type { IAppRegion } from '~/lib/appSettings';
import type { IInvitationResponseFormValues } from './InvitationResponseForm';
import { FormSection } from '~/components/atoms/FormSection';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { getOrganizationRoleLabel } from '~/components/organisms/fields/OrganizationRoleSelector';
import { useNavigate } from '~/components/utils/Navigation';
import { parseUserInfo } from '~/helpers/parseUserInfo';
import { useDeclineInvitationMutation } from '~/lib/api/modules/Invitations/hooks/useDeclineInvitationMutation';
import { InvitationResponseFormMainSection } from './InvitationResponseFormMainSection';

export type IOrganizationInvitationResponseFormProps = IContainerProps &
  Omit<
    IContentBlockFormBodyProps<IInvitationResponseFormValues>,
    'form' | 'children' | 'onSubmit' | 'submitting' | 'cancelUrl' | 'onCancel'
  > & {
    invitation: IApiInvitation;
    appRegion: IAppRegion;
    form: UseFormReturn<IInvitationResponseFormValues>;
    defaultValues?: DefaultValues<IInvitationResponseFormValues>;
    cancelUrl?: string;
  };

export const OrganizationInvitationResponseForm: React.FC<
  IOrganizationInvitationResponseFormProps
> = (props) => {
  const {
    sx,
    invitation,
    appRegion,
    form,
    defaultValues,
    cancelUrl,
    ...other
  } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const declineInvitationMutation = useDeclineInvitationMutation();
  const organizationId = invitation.target.organizationId;
  const userInfo = invitation
    ? parseUserInfo({
        _id: invitation.source._id,
        profile: invitation.source.profile,
      })
    : undefined;

  return (
    <ContentBlock.FormBody
      sx={sx}
      form={form}
      onSubmit={(values) =>
        navigate(
          `/organizations/${organizationId}/invitations/${invitation._id}/accept?${queryString.build(
            { challengeCode: values.challengeCode },
          )}`,
          { appRegion },
        )
      }
      submitButtonLabel={t('OrganizationInvitationResponseForm.accept')}
      onCancel={(values) =>
        declineInvitationMutation.mutateAsync(
          {
            organizationId,
            invitationId: invitation._id,
            appRegion,
            body: {
              challengeCode: values.challengeCode,
            },
          },
          {
            onSuccess: cancelUrl
              ? () => {
                  navigate(cancelUrl);
                }
              : undefined,
          },
        )
      }
      cancelButtonLabel={t('common.decline')}
      first
      {...other}
    >
      <FormSection>
        <p>{t('OrganizationInvitationResponseForm.dear-user')}</p>
        <Trans
          parent="p"
          i18nKey="OrganizationInvitationResponseForm.you-have-been-invited"
          values={{
            sourceName: userInfo?.fullName,
            sourceEmailAddress: userInfo?.emailAddress,
            organizationName: invitation.target.organizationName,
            role: getOrganizationRoleLabel(invitation.target.role, t),
          }}
        />
        <p>{t('OrganizationInvitationResponseForm.confirm')}</p>
      </FormSection>
      <InvitationResponseFormMainSection
        form={form}
        defaultValues={defaultValues}
        appRegion={appRegion}
      />
    </ContentBlock.FormBody>
  );
};
